import {
  getCardDefinition,
  getCardInstance,
  getCardInstanceDefinition,
  isSetupGameState,
} from "../selectors/card-instances";
import {
  LANE_IDS,
  type ActionValidationFailure,
  type ActionValidationResult,
  type DevelopLandAction,
  type FoundLandAction,
  type GameState,
  type PlayerId,
} from "../types";

function invalid(
  code: ActionValidationFailure["code"],
  message: string,
  path: string | null = null,
  details: ActionValidationFailure["details"] = {},
): ActionValidationResult {
  return { valid: false, failure: { code, message, path, details } };
}

function validateLandCardSelection(
  state: Readonly<GameState>,
  action: FoundLandAction | DevelopLandAction,
): ActionValidationResult {
  if (!isSetupGameState(state)) {
    return invalid(
      "CARD_INSTANCE_NOT_FOUND",
      "Land actions require a schema-V2 match with a card-instance manifest.",
      "cardInstanceId",
      { cardInstanceId: action.cardInstanceId },
    );
  }

  if (state.phase !== "land" && !(state.schemaVersion === 5 && state.phase === "action-window")) {
    return invalid(
      "WRONG_PHASE",
      "A Land Action is legal only during the Land Phase or public-beta action window.",
      "phase",
      {
        currentPhase: state.phase,
        requiredPhase: state.schemaVersion === 5 ? "action-window" : "land",
      },
    );
  }

  if (state.players[action.playerId].actionsUsed.landActions >= 1) {
    return invalid(
      "LAND_ACTION_ALREADY_USED",
      "A player may use only one Land Action per turn.",
      `players.${action.playerId}.actionsUsed.landActions`,
    );
  }

  const instance = getCardInstance(state, action.cardInstanceId);
  if (instance === undefined) {
    return invalid(
      "CARD_INSTANCE_NOT_FOUND",
      "The selected card instance does not exist in this match.",
      "cardInstanceId",
      { cardInstanceId: action.cardInstanceId },
    );
  }

  if (!state.players[action.playerId].hand.includes(action.cardInstanceId)) {
    return invalid(
      "CARD_NOT_IN_HAND",
      "The selected card must be in the acting player's hand.",
      `players.${action.playerId}.hand`,
      { cardInstanceId: action.cardInstanceId },
    );
  }

  const definition = getCardDefinition(state, instance.cardDefinitionId);
  if (definition === undefined) {
    return invalid(
      "CARD_DEFINITION_NOT_FOUND",
      "The selected card instance references an unknown definition.",
      `cardInstances.${action.cardInstanceId}.cardDefinitionId`,
      { cardDefinitionId: instance.cardDefinitionId },
    );
  }

  if (definition.type !== "land") {
    return invalid("CARD_NOT_LAND", "The selected card is not a Land.", "cardInstanceId", {
      cardInstanceId: action.cardInstanceId,
      cardDefinitionId: definition.id,
      cardType: definition.type,
    });
  }

  return { valid: true };
}

export function hasFoundingPriority(state: Readonly<GameState>, playerId: PlayerId): boolean {
  if (!isSetupGameState(state)) return false;

  const hasPlayableLand = state.players[playerId].hand.some(
    (cardInstanceId) => getCardInstanceDefinition(state, cardInstanceId)?.type === "land",
  );
  if (!hasPlayableLand) return false;

  return LANE_IDS.some((laneId) => {
    const lane = state.lanes[laneId];
    return lane.result === "active" && lane.players[playerId].status === "empty";
  });
}

export function validateFoundLandAction(
  state: Readonly<GameState>,
  action: FoundLandAction,
): ActionValidationResult {
  const cardValidation = validateLandCardSelection(state, action);
  if (!cardValidation.valid) return cardValidation;

  const lane = state.lanes[action.laneId];
  if (lane === undefined || lane.result !== "active") {
    return invalid(
      "TARGET_LANE_NOT_ACTIVE",
      "A Land can be founded only in an unresolved lane.",
      `lanes.${action.laneId}`,
    );
  }

  const side = lane.players[action.playerId];
  if (side.status !== "empty" || side.land !== null) {
    return invalid(
      side.status === "active" ? "TARGET_LANE_NOT_EMPTY" : "TARGET_LANE_NOT_ACTIVE",
      "A Land can be founded only on an empty player-side lane.",
      `lanes.${action.laneId}.players.${action.playerId}`,
      { laneSideStatus: side.status },
    );
  }

  return { valid: true };
}

export function validateDevelopLandAction(
  state: Readonly<GameState>,
  action: DevelopLandAction,
): ActionValidationResult {
  const cardValidation = validateLandCardSelection(state, action);
  if (!cardValidation.valid) return cardValidation;

  const lane = state.lanes[action.laneId];
  if (lane === undefined || lane.result !== "active") {
    return invalid(
      "TARGET_LANE_NOT_ACTIVE",
      "A Land can be developed only in an unresolved lane.",
      `lanes.${action.laneId}`,
    );
  }

  const side = lane.players[action.playerId];
  if (side.status !== "active") {
    return invalid(
      side.status === "empty" ? "TARGET_LANE_HAS_NO_LAND" : "TARGET_LANE_NOT_ACTIVE",
      "A Land can be developed only on an active player-side lane.",
      `lanes.${action.laneId}.players.${action.playerId}.status`,
      { laneSideStatus: side.status },
    );
  }

  const land = side.land;
  if (land === null) {
    return invalid(
      "TARGET_LANE_HAS_NO_LAND",
      "The target lane has no Land to develop.",
      `lanes.${action.laneId}.players.${action.playerId}.land`,
    );
  }

  if (land.stage === 3) {
    return invalid(
      "LAND_ALREADY_STAGE_THREE",
      "A Stage III Land cannot be developed further.",
      `lanes.${action.laneId}.players.${action.playerId}.land.stage`,
    );
  }

  if (hasFoundingPriority(state, action.playerId)) {
    return invalid(
      "FOUNDING_PRIORITY_REQUIRED",
      "An empty lane must be founded before an existing Land may be developed.",
      "laneId",
    );
  }

  const selectedDefinition = getCardInstanceDefinition(state, action.cardInstanceId);
  const baseCardInstanceId = land.cardInstanceIds[0];
  const currentDefinition =
    baseCardInstanceId === undefined
      ? undefined
      : getCardInstanceDefinition(state, baseCardInstanceId);

  if (selectedDefinition?.type !== "land" || currentDefinition?.type !== "land") {
    return invalid(
      "CARD_DEFINITION_NOT_FOUND",
      "The selected or current Land definition cannot be resolved.",
      `lanes.${action.laneId}.players.${action.playerId}.land.cardInstanceIds`,
    );
  }

  if (
    "landTypeId" in selectedDefinition &&
    typeof selectedDefinition.landTypeId === "string" &&
    "landTypeId" in currentDefinition &&
    typeof currentDefinition.landTypeId === "string" &&
    selectedDefinition.landTypeId !== currentDefinition.landTypeId
  ) {
    return invalid(
      "LAND_TYPE_MISMATCH",
      "Only a Land with the same Land Type can develop this stack.",
      "cardInstanceId",
      {
        selectedLandTypeId: selectedDefinition.landTypeId,
        currentLandTypeId: currentDefinition.landTypeId,
      },
    );
  }

  if (selectedDefinition.developmentId !== currentDefinition.developmentId) {
    return invalid(
      "LAND_DEVELOPMENT_MISMATCH",
      "Only a Land with the same development identity can develop this stack.",
      "cardInstanceId",
      {
        selectedDevelopmentId: selectedDefinition.developmentId,
        currentDevelopmentId: currentDefinition.developmentId,
      },
    );
  }

  return { valid: true };
}
